'use client';

import { useState } from 'react';

interface ShareButtonsProps {
  url: string;
  text: string;
  className?: string;
  showCard?: boolean;
  title?: string;        // overrides default heading in showCard mode
  subtitle?: string;     // overrides default subheading in showCard mode
  footer?: string | null; // overrides footer line; null = hide it entirely
  trackEvent?: string;    // umami event fired on each share click, with {network, source}
  trackSource?: string;   // where the share happened, e.g. 'page'
}

export default function ShareButtons({
  url,
  text,
  className = '',
  showCard = false,
  title,
  subtitle,
  footer,
  trackEvent,
  trackSource = 'page',
}: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);

  const enc = encodeURIComponent;
  const textWithUrl = `${text} ${url}`;

  const links = [
    { label: 'Bluesky',    href: `https://bsky.app/intent/compose?text=${enc(`${text} ${url} via @postcardstocongress.org`)}`, bg: 'bg-[#0085ff] hover:bg-[#0070d8]' },
    { label: 'X / Twitter', href: `https://x.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,                               bg: 'bg-black hover:bg-slate-800' },
    { label: 'Facebook',   href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,                                    bg: 'bg-[#1877f2] hover:bg-[#1464cc]' },
    { label: 'LinkedIn',   href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,                             bg: 'bg-[#0077b5] hover:bg-[#006097]' },
    { label: 'SMS',        href: `sms:?body=${enc(textWithUrl)}`,                                                                bg: 'bg-green-600 hover:bg-green-700' },
    { label: 'Email',      href: `mailto:?subject=${enc('I just sent a postcard to Congress')}&body=${enc(textWithUrl)}`,        bg: 'bg-slate-500 hover:bg-slate-600' },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const buttons = (
    <div className="flex flex-wrap gap-2">
      {links.map(({ label, href, bg }) => (
        <a
          key={label}
          href={href}
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
          onClick={trackEvent ? () => window.umami?.track(trackEvent, { network: label, source: trackSource }) : undefined}
          className={`inline-block px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-colors active:scale-95 ${bg}`}
        >
          {label}
        </a>
      ))}
    </div>
  );

  const resolvedFooter = footer === undefined
    ? 'default'   // sentinel: render the default campaign page link
    : footer;     // string (render it) or null (hide it)

  if (showCard) {
    return (
      <div className={`bg-amber-50 border border-amber-300 rounded-xl p-6 ${className}`}>
        <p className="font-bold text-amber-800 text-base mb-1">
          {title ?? 'Your postcard is one voice. Make it a chorus.'}
        </p>
        <p className="text-amber-900/70 text-sm mb-4">
          {subtitle ?? 'Every share turns into more postcards on more desks. One click:'}
        </p>
        {buttons}
        <p className="text-xs text-amber-900 mt-4 mb-1.5">Or copy this link:</p>
        <div className="flex items-center gap-2">
          <p className="flex-1 font-mono text-xs bg-white border border-amber-300 rounded px-2.5 py-2 text-amber-800 break-all">{url}</p>
          <button
            onClick={handleCopy}
            className={`shrink-0 px-3 py-2 rounded-lg text-xs font-bold transition-colors active:scale-95 ${
              copied ? 'bg-green-600 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'
            }`}
          >
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        </div>
        {resolvedFooter === 'default' ? (
          <p className="text-xs text-amber-900/70 mt-3">
            <a href={url} className="font-semibold text-amber-800 underline underline-offset-2">Your campaign page</a>{' '}
            shows a live count of how many people have joined you →
          </p>
        ) : resolvedFooter !== null ? (
          <p className="text-xs text-amber-900/70 mt-3">{resolvedFooter}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={className}>
      {buttons}
      <button
        onClick={handleCopy}
        className={`mt-2 inline-block px-3 py-1.5 rounded-lg text-xs font-bold transition-colors active:scale-95 ${
          copied ? 'bg-green-600 text-white' : 'bg-amber-500 hover:bg-amber-400 text-black'
        }`}
      >
        {copied ? 'Copied ✓' : 'Copy Link'}
      </button>
    </div>
  );
}
